import { useState, useEffect } from "react";
import { ArrowLeft, Loader2, Copy, Calendar, Clock, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

interface PatternLane {
  key: string;
  origin: string;
  destination: string;
  equipment: string;
  dayHits: number;
  totalPosts: number;
  dayOfWeek: number;
  streak: number;
  avgRpm: number | null;
  lastPostId: string;
  lastPostedAt: string;
  avgHour: number;
}

interface CarrierPostDuplicatesPaneProps {
  onBack: () => void;
}

/**
 * Carrier-side ATLAS Pattern Detection.
 * Mirrors the broker `PostDuplicatesPane` logic, but for truck postings:
 * groups a carrier's recent posts by origin + destination + equipment,
 * detects weekday + weekly-streak posting patterns, and surfaces
 * "today's repost" candidates.
 */
export function CarrierPostDuplicatesPane({ onBack }: CarrierPostDuplicatesPaneProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lanes, setLanes] = useState<PatternLane[]>([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().getDay();

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const analyze = async () => {
      try {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 120); // 4 months of history

        const { data } = await supabase
          .from("truck_postings")
          .select("id, origin_location, destination_location, equipment_type, rate_per_mile, created_at")
          .eq("created_by", user.id)
          .gte("created_at", cutoff.toISOString())
          .order("created_at", { ascending: false })
          .limit(500);

        const trucks = data || [];

        // Group by lane+equipment, tracking posting dates
        const groups = new Map<string, {
          origin: string;
          destination: string;
          equipment: string;
          postDates: Date[];
          rpms: number[];
          lastPostId: string;
        }>();

        for (const t of trucks) {
          const origin = (t.origin_location || "").trim();
          const destination = (t.destination_location || "").trim();
          if (!origin || !destination) continue;

          const key = `${origin}|${destination}|${t.equipment_type}`.toLowerCase();
          const postDate = new Date(t.created_at || "");
          const existing = groups.get(key);
          if (existing) {
            existing.postDates.push(postDate);
            if (t.rate_per_mile) existing.rpms.push(Number(t.rate_per_mile));
          } else {
            groups.set(key, {
              origin,
              destination,
              equipment: t.equipment_type,
              postDates: [postDate],
              rpms: t.rate_per_mile ? [Number(t.rate_per_mile)] : [],
              lastPostId: t.id,
            });
          }
        }

        const results: PatternLane[] = [];

        for (const [key, g] of groups.entries()) {
          if (g.postDates.length < 1) continue;

          const dayCounts = [0, 0, 0, 0, 0, 0, 0];
          const dayHours: number[][] = [[], [], [], [], [], [], []];
          for (const d of g.postDates) {
            dayCounts[d.getDay()]++;
            dayHours[d.getDay()].push(d.getHours());
          }

          let bestDay = 0;
          let bestCount = 0;
          for (let i = 0; i < 7; i++) {
            if (dayCounts[i] > bestCount) {
              bestCount = dayCounts[i];
              bestDay = i;
            }
          }

          const postsOnDay = g.postDates
            .filter(d => d.getDay() === bestDay)
            .sort((a, b) => b.getTime() - a.getTime());

          let streak = 1;
          if (bestCount >= 2) {
            for (let i = 0; i < postsOnDay.length - 1; i++) {
              const diffDays = (postsOnDay[i].getTime() - postsOnDay[i + 1].getTime()) / (1000 * 60 * 60 * 24);
              if (diffDays >= 5 && diffDays <= 9) {
                streak++;
              } else {
                break;
              }
            }
          }

          const avgHour = dayHours[bestDay].length > 0
            ? Math.round(dayHours[bestDay].reduce((a, b) => a + b, 0) / dayHours[bestDay].length)
            : 8;

          results.push({
            key,
            origin: g.origin,
            destination: g.destination,
            equipment: g.equipment,
            dayHits: bestCount,
            totalPosts: g.postDates.length,
            dayOfWeek: bestDay,
            streak,
            avgRpm: g.rpms.length > 0 ? g.rpms.reduce((a, b) => a + b, 0) / g.rpms.length : null,
            lastPostId: g.lastPostId,
            lastPostedAt: postsOnDay[0]?.toISOString() || g.postDates[0].toISOString(),
            avgHour,
          });
        }

        results.sort((a, b) => {
          const aStrong = a.streak >= 2 ? 1 : 0;
          const bStrong = b.streak >= 2 ? 1 : 0;
          if (aStrong !== bStrong) return bStrong - aStrong;
          if (aStrong && bStrong) {
            const aToday = a.dayOfWeek === today ? 1 : 0;
            const bToday = b.dayOfWeek === today ? 1 : 0;
            if (aToday !== bToday) return bToday - aToday;
            return b.streak - a.streak;
          }
          return new Date(b.lastPostedAt).getTime() - new Date(a.lastPostedAt).getTime();
        });

        setLanes(results.slice(0, 12));
      } catch (err) {
        console.error("CarrierPostDuplicatesPane error:", err);
      } finally {
        setLoading(false);
      }
    };

    analyze();
  }, [user, today]);

  const formatHour = (h: number) => {
    const ampm = h >= 12 ? "PM" : "AM";
    const hr = h % 12 || 12;
    return `~${hr} ${ampm}`;
  };

  const strongLanes = lanes.filter(l => l.streak >= 2);
  const recentLanes = lanes.filter(l => l.streak < 2);
  const todayLanes = strongLanes.filter(l => l.dayOfWeek === today);
  const otherLanes = strongLanes.filter(l => l.dayOfWeek !== today);

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button onClick={onBack} className="p-1 rounded hover:bg-muted transition-colors">
          <ArrowLeft className="h-4 w-4 text-muted-foreground" />
        </button>
        <Sparkles className="h-4 w-4 text-primary" />
        <h4 className="text-caption font-bold text-foreground">ATLAS Pattern Detection</h4>
        {todayLanes.length > 0 && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary animate-pulse">
            {todayLanes.length} ready for today
          </span>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-xs">Analyzing your truck posting patterns…</span>
          </div>
        ) : lanes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-2">
            <Sparkles className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No truck lanes detected yet.</p>
            <p className="text-xs text-muted-foreground/70">
              Post trucks and ATLAS will learn your lane patterns over time.
            </p>
          </div>
        ) : (
          <div>
            {todayLanes.length > 0 && (
              <div className="bg-primary/[0.03]">
                <div className="px-3 py-2 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-primary" />
                  <span className="text-[11px] font-bold text-primary uppercase tracking-wider">
                    It's {DAY_NAMES[today]} — You usually post these trucks
                  </span>
                </div>
                <ColumnHeader />
                {todayLanes.map((lane, i) => (
                  <LanePatternRow key={lane.key} lane={lane} isToday formatHour={formatHour} navigate={navigate} index={i} />
                ))}
              </div>
            )}

            {otherLanes.length > 0 && (
              <div>
                <div className="px-3 py-2 flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    Other weekly truck patterns detected
                  </span>
                </div>
                <ColumnHeader />
                {otherLanes.map((lane, i) => (
                  <LanePatternRow key={lane.key} lane={lane} isToday={false} formatHour={formatHour} navigate={navigate} index={i} />
                ))}
              </div>
            )}

            {recentLanes.length > 0 && (
              <div>
                <div className="px-3 py-2 flex items-center gap-1.5">
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
                    Recently posted lanes
                  </span>
                </div>
                <ColumnHeader />
                {recentLanes.map((lane, i) => (
                  <LanePatternRow key={lane.key} lane={lane} isToday={false} formatHour={formatHour} navigate={navigate} index={i} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
}

function ColumnHeader() {
  return (
    <div className="grid grid-cols-[1fr_70px_60px_90px_90px] items-center px-3 py-1 border-b border-border/50">
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Lane</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Posted</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Last</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Avg RPM</span>
      <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider text-right">Action</span>
    </div>
  );
}

function LanePatternRow({
  lane,
  isToday,
  formatHour,
  navigate,
  index,
}: {
  lane: PatternLane;
  isToday: boolean;
  formatHour: (h: number) => string;
  navigate: ReturnType<typeof import("react-router-dom").useNavigate>;
  index: number;
}) {
  return (
    <div className={`grid grid-cols-[1fr_70px_60px_90px_90px] items-center px-3 py-2 border-b border-border/30 hover:bg-muted/30 transition-colors ${index % 2 === 1 ? "bg-muted/20" : ""} ${isToday ? "bg-primary/[0.02]" : ""}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <span className="truncate">{lane.origin}</span>
          <span className="text-muted-foreground">→</span>
          <span className="truncate">{lane.destination}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-muted-foreground capitalize">{lane.equipment}</span>
          {lane.streak >= 2 ? (
            <>
              <span className="text-[10px] text-muted-foreground">•</span>
              <span className="text-[10px] font-medium text-muted-foreground">
                Every {DAY_NAMES[lane.dayOfWeek]} {formatHour(lane.avgHour)}
              </span>
              <span className="text-[10px] text-muted-foreground">•</span>
              <span className="text-[10px] font-medium text-foreground/70">
                {lane.streak}wk streak
              </span>
            </>
          ) : (
            <>
              <span className="text-[10px] text-muted-foreground">•</span>
              <span className="text-[10px] font-medium text-muted-foreground">Recently posted</span>
            </>
          )}
        </div>
      </div>

      <span className="text-[10px] font-medium text-foreground/70 tabular-nums text-right">
        {lane.totalPosts}×
      </span>

      <span className="text-[10px] font-medium text-muted-foreground text-right">
        {lane.lastPostedAt ? timeAgo(lane.lastPostedAt) : "—"}
      </span>

      <span className="text-[10px] font-semibold text-primary tabular-nums text-right">
        {lane.avgRpm ? `$${lane.avgRpm.toFixed(2)}` : "—"}
      </span>

      <div className="flex justify-end">
        <Button
          size="sm"
          variant={isToday ? "default" : "outline"}
          className="h-6 text-[10px] px-2.5 flex-shrink-0"
          onClick={() => navigate(`/post-truck?duplicate=${lane.lastPostId}`)}
        >
          {isToday ? "Repost" : "Post"}
        </Button>
      </div>
    </div>
  );
}
